import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeToJobOrders, approveJobOrder, updateJobOrder, deleteJobOrder } from '../../services/job-order-service';
import { updateJob as updateCutlistJob } from '../../services/cutlist-jobs';
import { createJobWithBridge, jobOrderWindowsToCutlist } from '../../services/cutlist-bridge';
import { calculateAllWindows } from '../../calculations/index';
import { generateCutListPDF } from '../../pdf/cutListPDF';
import { generateGlassOrderPDF } from '../../pdf/glassOrderPDF';
import { generateFlexScreenOrderPDF } from '../../pdf/flexScreenOrderPDF';
import { generateShopOrderPDF } from '../../pdf/shopOrderPDF';
import { generateJobOrderPDF } from '../../services/job-order-pdf';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import JobApprovalModal from '../../components/JobApprovalModal';
import { useNotification } from '../../components/NotificationModal';
import AppLayout from '../../components/AppLayout';
import {
  Clock, CheckCircle2, Calendar, ClipboardList, ChevronDown, Undo2,
  Save, FileDown, Pencil, Trash2, Search, ChevronRight, ChevronLeft,
} from 'lucide-react';

// ── Palette — same warm, coherent family as the Estimates page.
const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_MONO    = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

// QuickBooks sync Cloud Function endpoint. Fires automatically on office
// manager approval. Failure here does NOT undo the approval — the office
// manager's action stands; QBO is a downstream side effect.
const QBO_SYNC_URL = 'https://us-central1-twp-calendar-61a9c.cloudfunctions.net/quickbooksCreateInvoice';

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', color: OCHRE,  bg: '#FBF1E0' },
  approved:       { label: 'Approved',       color: MOSS,   bg: '#EAF1EA' },
  scheduled:      { label: 'Scheduled',      color: WALNUT, bg: '#F3E8DC' },
  completed:      { label: 'Completed',      color: STONE,  bg: '#F5F3F0' },
};

const FILTERS = [
  { key: 'pending_review', label: 'Pending Review', Icon: Clock },
  { key: 'approved',       label: 'Approved',       Icon: CheckCircle2 },
  { key: 'scheduled',      label: 'Scheduled',      Icon: Calendar },
  { key: 'all',            label: 'All Orders',     Icon: ClipboardList },
];

function fmt$(n) {
  return '$' + (parseFloat(n)||0).toLocaleString('en-US', { minimumFractionDigits: 2 });
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function JobOrdersReviewPage() {
  const navigate  = useNavigate();
  const { userProfile } = useAuth();
  const [orders, setOrders]   = useState([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('pending_review');
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const PAGE_SIZE = 25;
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes]     = useState({});
  const [saving, setSaving]   = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [docProgress, setDocProgress] = useState(null);
  const notify = useNotification();

  const [approveModal, setApproveModal] = useState(null);

  useEffect(() => {
    setLoading(true);
    setOrders([]);
    const unsub = subscribeToJobOrders(filter === 'all' ? null : filter, (data) => {
      setOrders(data);
      setLoading(false);
    });
    return () => unsub();
  }, [filter]);

  // Reset to first page when filter/search changes
  useEffect(() => { setPage(1); }, [filter, search]);

  async function load() {
    // no-op: real-time listener handles updates
  }
  async function handleDelete(jo) {
    if (!(await confirm({ title: `Delete job order for ${jo.customerName || 'this customer'}?`, message: `WO #${jo.contractNumber || jo.woNumber || '?'} — This will remove it from everywhere.`, tone: 'danger', confirmText: 'Delete' }))) return;
    try {
      setSaving(jo.id + '_delete');
      await deleteJobOrder(jo.id);
    } catch(e) {
      notify.error('Could not delete', 'Try again. If it persists, contact Kwizera.', e?.message);
    } finally {
      setSaving(null);
    }
  }

  async function handleRevertToPending(jo) {
    setSaving(jo.id);
    try {
      await updateJobOrder(jo.id, { 
        status: 'pending_review',
        officeManagerApprovedAt: null,
        officeManagerApprovedBy: null,
      });
      setOrders([]);
      if (filter === 'pending_review') {
        await load();
      } else {
        setFilter('pending_review');
      }
    } catch(e) { notify.error('Something went wrong', 'Try again. If it keeps happening, contact Kwizera.', e?.message); }
    setSaving(null);
  }

  function handleApprove(jo) {
    setApproveModal({ jo, durationDays: 1 });
  }

  async function handleApproveConfirm() {
    const { jo, durationDays } = approveModal;
    setApproveModal(null);
    setSaving(jo.id);
    setPdfError(null);
    setDocProgress(null);
    try {
      const c = jo.customer || {};
      const windows = (jo.windows || []);
      // hasWindows = at least one TWP window OR mulled pane (anything that needs cutlist)
      const hasWindows = windows.some(w => w.lineType === 'twp_window' || w.isMulledPane);
      const windowCount = windows.filter(w =>
        w.lineType === 'twp_window' || w.lineType === 'ordered_window'
      ).reduce((sum, w) => sum + (parseInt(w.qty) || 1), 0);
      const doorCount = windows.filter(w =>
        w.lineType === 'door'
      ).reduce((sum, w) => sum + (parseInt(w.qty) || 1), 0);

      // 1. Save notes if any
      if (notes[jo.id]) {
        await updateJobOrder(jo.id, { officeManagerNotes: notes[jo.id] });
      }

      // 2. Create calendar job + cutlist job (cutlist is skipped if no TWP/mulled windows)
      const { twpJobId, cutlistJobId } = await createJobWithBridge({
        skipCutlist: !hasWindows,
        customerName: (c.name && !c.name.includes('@') ? c.name : (c.firstName ? `${c.firstName} ${c.lastName||''}`.trim() : c.name || '')).split('\n')[0].trim(),
        woNumber: jo.contractNumber,
        phone: c.phone || c.cellPhone || '',
        email: c.email || '',
        address: c.address || '',
        city: c.city || '',
        stateZip: [c.state, c.zip].filter(Boolean).join(' '),
        locationTown: c.city || c.name || '',
        windowCount,
        doorCount,
        durationDays: durationDays,
        jobType: 'install',
        comments: notes[jo.id] || jo.officeManagerNotes || '',
        soldDate: jo.orderDate || (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
      });

      // 3. Populate cutlist job with TWP windows from job order
      const cutlistWindows = hasWindows ? jobOrderWindowsToCutlist(windows, 'White', jo.contractNumber) : [];
      if (hasWindows && cutlistJobId && cutlistWindows.length > 0) {
        await updateCutlistJob(cutlistJobId, { windows: cutlistWindows });
      }

      // 4. Auto-calculate cutlist and generate PDFs
      if (hasWindows && cutlistJobId && cutlistWindows && cutlistWindows.length > 0) {
        try {
          const calculatedWindows = calculateAllWindows(cutlistWindows);
          const c = jo.customer || {};
          const cutlistJob = {
            id: cutlistJobId,
            customerName: (c.name && !c.name.includes('@') ? c.name : (c.firstName ? `${c.firstName} ${c.lastName||''}`.trim() : c.name || '')).split('\n')[0].trim(),
            woNumber: jo.contractNumber,
            jobName: c.city || '',
            orderDate: jo.orderDate || '',
            color: cutlistWindows[0]?.color || 'White',
            windows: cutlistWindows,
            twpJobId,
          };
          await updateCutlistJob(cutlistJobId, {
            windows: cutlistWindows,
            windowCount: calculatedWindows.length,
            calculatedWindows,
            status: 'sold',
          });
          // Save document records
          const { saveDocument } = await import('../../services/cutlist-documents');
          await Promise.all([
            saveDocument(cutlistJobId, 'cutlist'),
            saveDocument(cutlistJobId, 'glass'),
            saveDocument(cutlistJobId, 'flexscreen'),
            saveDocument(cutlistJobId, 'shoporder'),
          ]);
          // Generate and upload PDFs (fire-and-forget). docProgress drives the
          // live checklist in JobApprovalModal — one entry per shop document.
          setDocProgress({ cutlist: 'working', glassorder: 'working', shoporder: 'working', flexscreen: 'working' });
          setTimeout(async () => {
            try {
              const { uploadJobPDF, savePDFUrlToJob } = await import('../../services/pdf-storage');
              const woNum = (jo.contractNumber || 'DRAFT').replace(/\s+/g, '_');
              const clBlob = await generateCutListPDF(cutlistJob, calculatedWindows, true);
              const glBlob = await generateGlassOrderPDF(cutlistJob, calculatedWindows, true);
              const shBlob = generateShopOrderPDF(cutlistJob, calculatedWindows, true);
              const flBlob = generateFlexScreenOrderPDF(cutlistJob, calculatedWindows, true);
              const step = (key, docType, blob) =>
                uploadJobPDF(woNum, docType, blob)
                  .then(url => savePDFUrlToJob(twpJobId, cutlistJobId, docType, url))
                  .then(() => setDocProgress(p => (p ? { ...p, [key]: 'ready' } : p)))
                  .catch(err => { setDocProgress(p => (p ? { ...p, [key]: 'error' } : p)); throw err; });
              await Promise.all([
                step('cutlist',    'cutlist',    clBlob),
                step('glassorder', 'glassorder', glBlob),
                step('shoporder',  'shoporder',  shBlob),
                step('flexscreen', 'flexscreen', flBlob),
              ]);
            } catch(pdfErr) {
              console.error('PDF gen error:', pdfErr);
              setDocProgress(p => (p ? {
                cutlist:    p.cutlist    === 'ready' ? 'ready' : 'error',
                glassorder: p.glassorder === 'ready' ? 'ready' : 'error',
                shoporder:  p.shoporder  === 'ready' ? 'ready' : 'error',
                flexscreen: p.flexscreen === 'ready' ? 'ready' : 'error',
              } : p));
              setPdfError(pdfErr?.message || 'PDF generation failed — report to Kwizera.');
            }
          }, 500);
        } catch(calcErr) { console.error('Calc error:', calcErr); }
      }

      // 5. Approve job order and link to calendar job
      await approveJobOrder(jo.id, userProfile?.name || userProfile?.email || 'Office Manager');
      await updateJobOrder(jo.id, { calendarJobId: twpJobId, cutlistJobId, status: 'approved' });

      // 6. Sync to QuickBooks Online (don't block on failure — approval stands)
      let qboSync = null;
      try {
        const qboResp = await fetch(QBO_SYNC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobOrderId: jo.id }),
        });
        qboSync = await qboResp.json();
        if (!qboResp.ok && qboSync) qboSync.ok = false;
      } catch (qboErr) {
        console.error('QBO sync request failed:', qboErr);
        qboSync = { ok: false, error: qboErr.message || 'Network error reaching QuickBooks sync function' };
      }

      // 7. Show success banner (includes QBO sync result)
      setOrders([]);
      setSuccessInfo({
        contractNumber: jo.contractNumber,
        customerName: c.name,
        windowCount,
        doorCount,
        hasWindows,
        cutlistJobId,
        twpJobId,
        qboSync,
        approvedBy: userProfile?.name || userProfile?.email || 'Office Manager',
        approvedAt: Date.now(),
      });

      await load();

      // 8. Modal handles navigation — user clicks 'Go to Cut List' explicitly.

    } catch(e) {
      console.error(e);
      notify.error('Approval failed', 'Could not approve this job order. Try again — if it keeps happening, contact Kwizera.', e?.message);
    }
    setSaving(null);
  }

  async function handleSaveNotes(jo) {
    setSaving(jo.id + '_notes');
    try {
      await updateJobOrder(jo.id, { officeManagerNotes: notes[jo.id] || '' });
      setSaving('notes_saved_' + jo.id);
      setTimeout(() => setSaving(null), 2000);
    } catch(e) { notify.error('Something went wrong', 'Try again. If it keeps happening, contact Kwizera.', e?.message); }
    setSaving(null);
  }

  const ApproveModal = approveModal ? (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <div style={{background:PAPER,borderRadius:'20px',padding:'32px',maxWidth:'440px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)',fontFamily:FONT_SANS}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px',fontFamily:FONT_DISPLAY,fontSize:'22px',fontWeight:600,color:INK,marginBottom:'6px'}}>
          <CheckCircle2 size={20} strokeWidth={2} color={MOSS} /> Approve Job Order
        </div>
        <div style={{fontSize:'14px',color:STONE,marginBottom:'24px'}}>{approveModal.jo.customerName} — WO #{approveModal.jo.contractNumber}</div>
        <div style={{marginBottom:'24px'}}>
          <div style={{fontSize:'12px',fontWeight:600,color:STONE,marginBottom:'8px'}}>Estimated days of installation</div>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <input type="number" name="twp-jobapprove-duration-days" autoComplete="off" min="1" max="99" value={approveModal.durationDays}
              onChange={e => setApproveModal(p => ({...p, durationDays: Math.max(1, parseInt(e.target.value)||1)}))}
              style={{width:'90px',padding:'12px',borderRadius:'12px',border:`2px solid ${WALNUT}`,fontFamily:FONT_DISPLAY,fontSize:'22px',fontWeight:600,textAlign:'center',color:WALNUT,outline:'none'}} />
            <div style={{fontSize:'14px',color:STONE}}>day{approveModal.durationDays>1?'s':''} on site</div>
          </div>
        </div>
        <div style={{display:'flex',gap:'10px'}}>
          <button onClick={() => setApproveModal(null)}
            style={{flex:1,padding:'13px',borderRadius:'12px',border:'1px solid #e0d5cc',background:PAPER,color:STONE,fontSize:'14px',fontWeight:'700',cursor:'pointer',fontFamily:FONT_SANS}}>
            Cancel
          </button>
          <button onClick={handleApproveConfirm}
            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',flex:2,padding:'13px',borderRadius:'12px',border:'none',background:MOSS,color:'white',fontSize:'14px',fontWeight:'700',cursor:'pointer',fontFamily:FONT_SANS}}>
            <CheckCircle2 size={16} strokeWidth={2} /> Approve &amp; Send to Calendar
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const breadcrumb = 'Dashboard › Estimates › Job Orders';

  // Search over customer name / contract number, then paginate
  const q = search.trim().toLowerCase();
  const filteredOrders = q
    ? orders.filter(jo =>
        (jo.customer?.name || '').toLowerCase().includes(q) ||
        (jo.contractNumber || '').toLowerCase().includes(q)
      )
    : orders;
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pagedOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AppLayout title="Job Orders Review" breadcrumb={breadcrumb}>
    <>{ApproveModal}<div style={{ fontFamily: FONT_SANS }}>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: filter === f.key ? WALNUT : PAPER,
              color: filter === f.key ? 'white' : '#4a5568',
              border: '1px solid ' + (filter === f.key ? WALNUT : '#e8e2da'),
              borderRadius: '999px', padding: '6px 14px',
              fontSize: '12px', fontWeight: '700',
              cursor: 'pointer', fontFamily: FONT_SANS,
              transition: 'all 150ms',
            }}>
            <f.Icon size={13} strokeWidth={2} /> {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{
        background: PAPER, borderRadius: '12px', padding: '10px 14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '18px',
        border: '1px solid #e8e3dd',
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>
        <Search size={15} strokeWidth={2} color={STONE} style={{ flexShrink: 0 }} />
        <input
          name="twp-joborders-search"
          autoComplete="off"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search customer or contract number…"
          style={{
            flex: 1, border: 'none', outline: 'none',
            fontSize: '13px', fontFamily: FONT_SANS,
            color: INK, background: 'transparent',
          }}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{
            background: 'none', border: 'none', color: '#bbb',
            cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0', flexShrink: 0,
          }}>×</button>
        )}
      </div>

      {/* Job approval modal — replaces inline banner */}
      <JobApprovalModal
        successInfo={successInfo}
        pdfError={pdfError}
        docProgress={docProgress}
        onClose={() => { setSuccessInfo(null); setPdfError(null); setDocProgress(null); }}
        onNavigate={navigate}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: STONE, fontSize: '13px' }}>Loading job orders…</div>
      ) : filteredOrders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: PAPER, borderRadius: '16px', border: '1px solid #e8e3dd' }}>
          <ClipboardList size={32} strokeWidth={1.5} color={STONE} style={{ marginBottom: '12px' }} />
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '18px', color: INK, marginBottom: '6px' }}>
            {search ? 'No results found' : 'No job orders found'}
          </div>
          <div style={{ fontSize: '13px', color: STONE }}>
            {search ? `Nothing matched "${search}"` : 'Nothing in this category right now.'}
          </div>
        </div>
      ) : (
        pagedOrders.map(jo => {
          const st = STATUS_CONFIG[jo.status] || STATUS_CONFIG.pending_review;
          const isOpen = expanded === jo.id;
          const c = jo.customer || {};
          const isPending = jo.status === 'pending_review';

          return (
            <div key={jo.id} style={{ background: PAPER, borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: '12px', overflow: 'hidden', border: isPending ? `2px solid ${OCHRE}` : '2px solid transparent' }}>

              {/* Summary row */}
              <div onClick={() => setExpanded(isOpen ? null : jo.id)}
                  style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontFamily: FONT_SANS }}>

                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: '80px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: STONE }}>Contract</div>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: 600, color: WALNUT }}>#{jo.contractNumber}</div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', fontSize: '15px', color: INK }}>{c.name || '—'}</div>
                    <div style={{ fontSize: '12px', color: STONE }}>{[c.address, c.city, c.state].filter(Boolean).join(', ')}</div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: STONE }}>Items</div>
                    <div style={{ fontWeight: '700', color: INK }}>{jo.windows?.length || 0}</div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: STONE }}>Total</div>
                    <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, color: WALNUT }}>{fmt$(jo.grandTotal)}</div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: STONE }}>Date</div>
                    <div style={{ fontSize: '12px', color: INK }}>{fmtDate(jo.createdAt)}</div>
                  </div>

                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: st.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: st.color }}>{st.label}</span>
                  </span>

                  <ChevronDown size={16} strokeWidth={2} color={STONE} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div style={{ borderTop: '1px solid #f0ebe6', padding: '20px', fontFamily: FONT_SANS }}>

                    {/* Window specs table */}
                    <div style={{ fontSize: '13px', fontWeight: 600, color: INK, marginBottom: '10px' }}>Line items</div>
                    <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', minWidth: '1100px' }}>
                        <thead>
                          <tr style={{ background: '#fbf9f6' }}>
                            {['#','Qty','Size','UI','Style','Glass','Outside','Inside','Flanges','Colonial','Diamond','Prairie','Scrn 1/2','Scrn Full','Room','Notes','Unit $','Total $'].map(h => (
                              <th key={h} style={{ padding: '5px 6px', textAlign: 'center', fontWeight: 600, color: STONE, fontSize: '10px', whiteSpace: 'nowrap', borderBottom: '2px solid #f0ebe6' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(jo.windows || []).map((w, i) => {
                            const unit  = parseFloat(w.unitPrice||w.price)||0;
                            const total = unit * (parseFloat(w.qty)||1);
                            const sz = w.width && w.height ? `${w.width}"×${w.height}"` : '';
                            const ui = w.width && w.height ? parseFloat(w.width)+parseFloat(w.height) : '';
                            return (
                              <tr key={i} style={{ background: i%2===0 ? PAPER : '#fbf9f6', borderBottom: '1px solid #f0ebe6' }}>
                                <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: '700', color: WALNUT, fontFamily: FONT_MONO }}>{i+1}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{w.qty||1}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>{sz}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{ui}</td>
                                <td style={{ padding: '5px 8px' }}>{w.style||w.description||''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{w.glass||''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{w.colorOutside||''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{w.colorInside||''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: MOSS, fontWeight: 700 }}>{w.flanges ? '✓' : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: MOSS, fontWeight: 700 }}>{w.gridColonial ? '✓' : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: MOSS, fontWeight: 700 }}>{w.gridDiamond  ? '✓' : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: MOSS, fontWeight: 700 }}>{w.gridPrairie  ? '✓' : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: MOSS, fontWeight: 700 }}>{w.screenHalf   ? '✓' : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: MOSS, fontWeight: 700 }}>{w.screenFull   ? '✓' : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{w.room||''}</td>
                                <td style={{ padding: '5px 8px', maxWidth: '120px', fontSize: '11px', color: STONE }}>{w.extrasNotes||''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', color: STONE }}>{unit>0 ? fmt$(unit) : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: '700', color: WALNUT, fontFamily: FONT_DISPLAY }}>{total>0 ? fmt$(total) : ''}</td>
                              </tr>
                            );
                          })}
                          {(jo.adders || []).map((a, wi) => {
                            const price = parseFloat(a.price) || 0;
                            const qty   = parseInt(a.qty) || 1;
                            const total = price * qty;
                            const rowIdx = (jo.windows||[]).length + wi;
                            return (
                              <tr key={'adder-'+wi} style={{ background: '#FBF1E0', borderBottom: '1px solid #f0ddb8' }}>
                                <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: '700', color: OCHRE, fontFamily: FONT_MONO }}>{rowIdx+1}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{qty}</td>
                                <td colSpan={2} style={{ padding: '5px 8px', textAlign: 'center', color: STONE }}>—</td>
                                <td style={{ padding: '5px 8px', fontStyle: 'italic', color: OCHRE, fontWeight: '600' }}>{a.name || a.description || '—'}</td>
                                <td colSpan={9} style={{ padding: '5px 8px', color: STONE, fontSize: '11px' }}>{a.notes || ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', color: STONE }}>{price > 0 ? fmt$(price) : ''}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: '700', color: OCHRE, fontFamily: FONT_DISPLAY }}>{total > 0 ? fmt$(total) : ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '32px', marginBottom: '20px', padding: '12px 16px', background: '#fbf9f6', borderRadius: '10px' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>Subtotal</div>
                        <div style={{ fontWeight: '700', color: INK }}>{fmt$(jo.subtotal)}</div>
                      </div>
                      {jo.taxEnabled && (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>Excise tax</div>
                          <div style={{ fontWeight: '700', color: INK }}>{fmt$(jo.taxAmount)}</div>
                        </div>
                      )}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>Grand total</div>
                        <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '18px', color: WALNUT }}>{fmt$(jo.grandTotal)}</div>
                      </div>
                    </div>

                    {/* Office Manager Notes */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: STONE, marginBottom: '6px' }}>Office manager notes</div>
                      <textarea
                        value={notes[jo.id] ?? (jo.officeManagerNotes || '')}
                        onChange={e => setNotes(prev => ({ ...prev, [jo.id]: e.target.value }))}
                        placeholder="Add notes, special instructions, or concerns before approving..."
                        rows={3}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e0d5cc', fontSize: '13px', fontFamily: FONT_SANS, color: INK, resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontFamily: FONT_SANS }}>
                      {jo.status === 'approved' && userProfile?.role === 'officeManager' && (
                        <button onClick={() => handleRevertToPending(jo)} disabled={saving === jo.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '7px', background: OCHRE, color: 'white', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS }}>
                          <Undo2 size={15} strokeWidth={2} /> Move to Pending
                        </button>
                      )}
                      {isPending && userProfile?.role === 'officeManager' && (
                        <button onClick={() => handleApprove(jo)} disabled={saving === jo.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '7px', background: MOSS, color: 'white', border: 'none', borderRadius: '10px', padding: '10px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS, boxShadow: '0 4px 12px rgba(59,107,68,0.3)' }}>
                          <CheckCircle2 size={15} strokeWidth={2} /> {saving === jo.id ? 'Approving…' : 'Approve Job Order'}
                        </button>
                      )}
                      <button onClick={() => handleSaveNotes(jo)} disabled={saving === jo.id + '_notes'}
                        style={{ display: 'flex', alignItems: 'center', gap: '7px', background: PAPER, color: WALNUT, border: `2px solid ${WALNUT}`, borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS }}>
                        <Save size={15} strokeWidth={2} /> {saving === jo.id + '_notes' ? 'Saving…' : saving === 'notes_saved_' + jo.id ? 'Saved!' : 'Save Notes'}
                      </button>
                      <button onClick={() => generateJobOrderPDF(jo)}
                        style={{ display: 'flex', alignItems: 'center', gap: '7px', background: PAPER, color: WALNUT, border: `2px solid ${WALNUT}`, borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS }}>
                        <FileDown size={15} strokeWidth={2} /> Download PDF
                      </button>
                      <button onClick={() => navigate(`/estimates/job-order/${jo.estimateId}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#f0f0f0', color: STONE, border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS }}>
                        <Pencil size={15} strokeWidth={2} /> Edit Job Order
                      </button>
                      {userProfile?.role === 'officeManager' && (
                        <button onClick={() => handleDelete(jo)} disabled={saving === jo.id + '_delete'}
                          style={{ display: 'flex', alignItems: 'center', gap: '7px', background: PAPER, color: RUST, border: `2px solid ${RUST}`, borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS }}>
                          <Trash2 size={15} strokeWidth={2} /> {saving === jo.id + '_delete' ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                      {jo.officeManagerApprovedAt && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: MOSS, fontSize: '12px', fontWeight: '600' }}>
                          <CheckCircle2 size={13} strokeWidth={2} /> Approved by {jo.officeManagerApprovedBy} on {fmtDate(jo.officeManagerApprovedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Pagination */}
        {!loading && filteredOrders.length > PAGE_SIZE && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '8px 14px',
                background: page === 1 ? '#f3f4f6' : PAPER,
                border: '1px solid #e0d5cc', borderRadius: '8px',
                color: page === 1 ? '#9ca3af' : WALNUT,
                fontSize: '12px', fontWeight: '700',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontFamily: FONT_SANS,
              }}><ChevronLeft size={13} strokeWidth={2} /> Previous</button>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#4a5568', padding: '8px 14px', fontFamily: FONT_SANS }}>
              Page {page} of {totalPages}
              <span style={{ color: '#9ca3af', fontWeight: '500', marginLeft: '8px' }}>
                ({filteredOrders.length} orders)
              </span>
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '8px 14px',
                background: page >= totalPages ? '#f3f4f6' : PAPER,
                border: '1px solid #e0d5cc', borderRadius: '8px',
                color: page >= totalPages ? '#9ca3af' : WALNUT,
                fontSize: '12px', fontWeight: '700',
                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                fontFamily: FONT_SANS,
              }}>Next <ChevronRight size={13} strokeWidth={2} /></button>
          </div>
        )}
    </div>
    </>
    </AppLayout>
  );
}
